import { useState } from 'react'

/**
 * The state a drawer is opened with: what it is showing, and whether it is open. The two are held
 * apart so that `close()` leaves the drawer mounted with its content while it slides shut, and
 * `forgetContent()` -- which the drawer calls back through `onFinishedClosing` -- drops it once the
 * closing animation has run.
 *
 * `showing` boxes the content, so a drawer with nothing of its own to carry is shown the same way.
 */
export function useDrawerContent<T = void>() {
  const [showing, setShowing] = useState<{ content: T } | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  return {
    /** The boxed content, from the moment the drawer is shown until it has finished closing. */
    showing,
    isOpen,
    show: (content: T) => {
      setShowing({ content })
      setIsOpen(true)
    },
    close: () => setIsOpen(false),
    forgetContent: () => setShowing(null),
  }
}
